import {
  IconWrapper,
  ItemBackgroundImage,
  ItemBgWrapper,
  ItemWrapper,
  ItemsWrapper,
  WorkingAtCodepointLine
} from "./styles";
import { WorkingAtCodepointType } from "../../../models/careers";
import { useTranslation } from "next-i18next";
import { colors } from "../../../styles/colors";
import { Box } from "../../../styles/basicStyles";
import { FC, useState } from "react";
import Typography from "../../../components/typography";
import Icon from "../../../components/icon";
import Dot from "../../../components/views/dot";
import VisibilitySensor from "react-visibility-sensor";
import Fade from "react-reveal/Fade";
import useLazyBgImage from "../../../hooks/images/useLazyBgImage";

const items: WorkingAtCodepointType[] = [
  {
    _id: 0,
    icon: "outlined_rocket",
    title: "CAREERS_WORKING_TITLE_1",
    description: "CAREERS_WORKING_DESCRIPTION_1",
  },
  {
    _id: 1,
    image: "/assets/careers/workingAtCodepoint/img_1.jpg",
  },
  {
    _id: 2,
    icon: "outlined_desktop",
    title: "CAREERS_WORKING_TITLE_2",
    description: "CAREERS_WORKING_DESCRIPTION_2",
  },
  {
    _id: 3,
    image: "/assets/careers/workingAtCodepoint/img_2.jpg",
  },
  {
    _id: 4,
    icon: "outlined_lightningBolt",
    title: "CAREERS_WORKING_TITLE_3",
    description: "CAREERS_WORKING_DESCRIPTION_3",
  },
  {
    _id: 5,
    image: "/assets/careers/workingAtCodepoint/img_3.jpg",
  },
  {
    _id: 6,
    icon: "outlined_heart",
    title: "CAREERS_WORKING_TITLE_4",
    description: "CAREERS_WORKING_DESCRIPTION_4",
  },
  {
    _id: 7,
    image: "/assets/careers/workingAtCodepoint/img_4.png",
  },
  {
    _id: 8,
    icon: "outlined_cake",
    title: "CAREERS_WORKING_TITLE_5",
    description: "CAREERS_WORKING_DESCRIPTION_5",
  },
  {
    _id: 9,
    image: "/assets/careers/workingAtCodepoint/img_5.jpg",
    bgPosition: '65%'
  },
  {
    _id: 10,
    icon: "outlined_benefits",
    title: "CAREERS_WORKING_TITLE_6",
    description: "CAREERS_WORKING_DESCRIPTION_6",
  },
  {
    _id: 11,
    image: "/assets/careers/workingAtCodepoint/img_6.jpg",
  },
];

const WorkingAtCodepoint: FC = () => {
  const [started, setStarted] = useState<boolean>(false);
  const { t } = useTranslation();
  const bgRefs = useLazyBgImage(items.map(item => item.image));

  const checkState = (isVisible: boolean) => {
    if(isVisible && !started) setStarted(true);
  };

  return (
    <VisibilitySensor onChange={checkState} delayedCall partialVisibility>
      <Box
        pt={{ md: 7.188, xxs: 3 }}
        pb={{ md: 10, xxs: 5.938 }}
        fDirection="column"
      >
        <Typography
          as='h2'
          fSize={{ md: 2.5, xxs: 1.813 }}
          lHeight={{ md: 3, xxs: 3 }}
          fWeight={700}
          style={{ color: colors.black }}
        >
          {t("WORKING_AT")}&nbsp;
          <span style={{ position: 'relative', marginRight: '0.5rem' }}>
            Codepoint
            <Dot
              w={{ md: 0.563, xxs: 0.5 }}
              h={{ md: 0.563, xxs: 0.5 }}
              ml={0.125}
            />
            <WorkingAtCodepointLine started={started}>
              <svg xmlns="http://www.w3.org/2000/svg" width="201" height="15" viewBox="0 0 201 15" fill="none">
                <path d="M2.15437 8.51054C18.1767 4.98876 35.3988 3.25673 51.8609 2.68186C54.8325 2.57809 60.4502 1.40402 63.2842 2.69982C68.0592 4.88312 52.8599 10.374 57.1028 12.4278C58.794 13.2466 62.5747 12.6101 64.3915 12.4386C70.1646 11.8937 75.9935 10.9659 81.7457 10.203C95.4736 8.38238 109.093 6.82259 122.954 6.33857C124.233 6.29391 129.206 5.3559 129.632 7.16649C130.067 9.01943 124.189 10.0858 122.671 10.4413C121.854 10.6326 117.359 11.7361 117.288 12.5242C117.262 12.8142 123.793 12.359 124.27 12.2804C133.929 10.6865 144.335 10.8605 154.136 10.7447C168.955 10.5696 183.841 10.8936 198.649 11.1988" stroke="#816CFF" strokeWidth="4" strokeLinecap="round"/>
              </svg>
            </WorkingAtCodepointLine>
          </span>
          {t("MEANS")}
        </Typography>
        <ItemsWrapper mt={{ md: 5.5, xxs: 1.313 }}>
          {items.map((e, index) => (
            <ItemWrapper
              n={index + 1}
              key={index}
              padding={e.image ? 0 : { md: "4.125rem 2rem", xxs: "3.125rem 2rem" }}
              bgColor={colors.alabaster}
            >
              {e.image ? (
                <ItemBgWrapper>
                  <Fade bottom>
                    <ItemBackgroundImage
                      ref={(el: HTMLElement | null) => (bgRefs.current[index] = el)}
                      h="100%"
                      w="100%"
                      bgPosition={e.bgPosition}
                    />
                  </Fade>
                </ItemBgWrapper>
              ) : (
                <Fade bottom>
                  <Box fAlign="flex-start" fJustify="flex-start">
                    {e.icon && (
                      <Box fDirection="column">
                        <IconWrapper opacity={0.6}>
                          <Box
                            h={2.813}
                            w={2.813}
                            fAlign="center"
                            fJustify="center"
                            zIndex={1}
                          >
                            <Icon icon={e.icon} color={colors.black} size={1.813} />
                          </Box>
                        </IconWrapper>
                        {
                          e.title &&
                          <Typography
                            mt={{ md: 1.313, xxs: 1.438 }}
                            fSize={{ md: 1.875, xxs: 1.75 }}
                            lHeight={{ md: 2.25, xxs: 2.063 }}
                            fWeight={700}
                            style={{ color: colors.black }}
                            maxW="100%"
                            fWrap="wrap"
                          >
                            {t(e.title)}
                          </Typography>
                        }
                        {
                          e?.description &&
                          <Typography
                            mt={{ md: 1.313, xxs: 1.438 }}
                            fSize={1.063}
                            lHeight={1.875}
                            fWeight={350}
                            style={{ color: colors.black }}
                            maxW={{ md: 16, xxs: "100%" }}
                            fWrap="wrap"
                          >
                            {t(e.description)}
                          </Typography>
                        }
                      </Box>
                    )}
                  </Box>
                </Fade>
              )}
            </ItemWrapper>
          ))}
        </ItemsWrapper>
      </Box>
    </VisibilitySensor>
  );
};

export default WorkingAtCodepoint;
